import React, { useCallback } from "react";
import { Agent, SecurityIssue } from "../../types/security";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft,
  AlertCircle,
  TicketPlus,
  Shield,
  Activity,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import {
  getSeverityColor,
  getRiskScoreColor,
  formatRiskScore,
} from "@/utils/securityHelpers";
import { getSuggestedFixByType } from "@/utils/securitySuggestions";
import { TrendIndicator } from "./TrendIndicator";

interface AgentDetailProps {
  agent: Agent;
  issues: SecurityIssue[];
  onClose: () => void;
}

interface StatCardProps {
  title: string;
  value: React.ReactNode;
}

const StatCard: React.FC<StatCardProps> = ({ title, value }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
    </CardHeader>
    <CardContent>{value}</CardContent>
  </Card>
);

interface AgentIssueCardProps {
  issue: SecurityIssue;
  onOpenTicket: (issue: SecurityIssue) => void;
}

const AgentIssueCard: React.FC<AgentIssueCardProps> = React.memo(
  ({ issue, onOpenTicket }) => {
    const formatIssueType = (type: string): string => type.replace(/_/g, " ");

    return (
      <Card className="border-l-4 border-l-red-500">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge
                  className={`${getSeverityColor(issue.severity)} text-xs px-2 py-0.5`}
                >
                  {issue.severity}
                </Badge>
                <Badge variant="outline" className="text-xs px-2 py-0.5">
                  {formatIssueType(issue.type)}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {formatDistanceToNow(new Date(issue.timestamp), { addSuffix: true })}
                </span>
              </div>
              <CardTitle className="text-base">{issue.title}</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">{issue.description}</p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onOpenTicket(issue)}
              className="ml-4"
            >
              <TicketPlus className="h-4 w-4 mr-2" />
              Open Ticket
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Trace Information */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium">Trace Information</span>
            </div>
            <div className="bg-muted p-3 rounded-md space-y-1 text-xs font-mono">
              <div>
                <span className="text-muted-foreground">Trace ID:</span>{" "}
                <span className="text-blue-600">{issue.traceId}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Scanner:</span> {issue.scanner}
              </div>
              <div>
                <span className="text-muted-foreground">Risk Score:</span>{" "}
                <span className={`${getRiskScoreColor(issue.riskScore)} font-bold`}>
                  {formatRiskScore(issue.riskScore)}
                </span>
              </div>
              {issue.metadata?.endpoint && (
                <div>
                  <span className="text-muted-foreground">Endpoint:</span>{" "}
                  {issue.metadata.endpoint}
                </div>
              )}
            </div>
          </div>

          {/* Detected Content */}
          {issue.detectedContent && (
            <div>
              <div className="text-sm font-medium mb-1">Detected Content</div>
              <div className="bg-red-50 border border-red-200 p-2 rounded text-xs font-mono">
                {issue.detectedContent}
              </div>
            </div>
          )}

          {/* Suggested Fix */}
          <div className="bg-blue-50 border border-blue-200 p-3 rounded-md">
            <div className="flex items-start gap-2">
              <Shield className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-sm font-medium text-blue-900 mb-1">Suggested Fix</div>
                <p className="text-xs text-blue-800">{getSuggestedFixByType(issue.type)}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
);

AgentIssueCard.displayName = "AgentIssueCard";

export const AgentDetail: React.FC<AgentDetailProps> = ({ agent, issues, onClose }) => {
  const handleOpenTicket = useCallback((issue: SecurityIssue) => {
    // This would integrate with your ticketing system
    alert(`Opening ticket for issue: ${issue.id} - ${issue.title}`);
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={onClose}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Shield className="h-6 w-6 text-blue-600" />
              {agent.name}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">{agent.description}</p>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline" className="text-xs">
                {agent.modelName}
              </Badge>
              <Badge variant="outline" className="text-xs">
                Agent ID: {agent.id}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <Separator />

      {/* Stats Overview */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard
          title="Total Issues"
          value={<div className="text-2xl font-bold">{agent.totalIssues}</div>}
        />
        <StatCard
          title="Critical"
          value={<div className="text-2xl font-bold text-red-600">{agent.criticalIssues}</div>}
        />
        <StatCard
          title="High"
          value={<div className="text-2xl font-bold text-orange-600">{agent.highIssues}</div>}
        />
        <StatCard
          title="Trend"
          value={
            <TrendIndicator
              direction={agent.trendDirection}
              percentage={agent.trendPercentage}
              size="lg"
              showSign
            />
          }
        />
      </div>

      {/* Issues List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Security Issues ({issues.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {issues.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No security issues found for this agent.</p>
            </div>
          ) : (
            issues.map((issue) => (
              <AgentIssueCard key={issue.id} issue={issue} onOpenTicket={handleOpenTicket} />
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
};
